import { Spinner } from "@fluentui/react-components";
import { getCoprincipalMailboxInfoForMailboxInfo } from "owa-account-source-list-store";
import { logCoreUsage } from "owa-analytics";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import { getIsDarkTheme } from "owa-fabric-theme";
import { OWAIframe } from "owa-iframe";
import loc, { getCurrentLanguageFromCurrentLocale } from "owa-localize";
import { observer } from "owa-mobx-react";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { useState } from "owa-react";
import { useCallback, useEffect, useRef, type FunctionComponent, type SyntheticEvent } from "react";
import getRewardsExpectedAnid from "../utils/getRewardsExpectedAnid";
import { useRewardsCacheSafeWindow } from "../utils/useRewardsCacheSafeWindow";
import waitForFlyoutReady from "../utils/waitForFlyoutReady";
import RewardsFallback from "./RewardsFallback";
import { loadingRewardsText, rewardsPaneHeaderText } from "./RewardsPane.locstring.json";
import { iframeContainer, loadingCover } from "./RewardsPane.scss";

const getRewardsIframeUrl = (isDarkMode: boolean, owaAnid: string) => {
    let returnUrl = `https://www.bing.com/rewards/panelflyout?partnerId=OutlookOwa&isDarkMode=${isDarkMode ? 1 : 0}&lat=1&lang=${encodeURIComponent(getCurrentLanguageFromCurrentLocale())}`;
    if (owaAnid) {
        returnUrl += `&owaAnid=${encodeURIComponent(owaAnid)}`;
    }
    return `https://www.bing.com/fd/auth/signin?action=interactive&provider=windows_live_id&return_url=${encodeURIComponent(returnUrl)}`;
};

const rewardsIframeStyle = { border: 0, display: "block", height: "100%", width: "100%" };
type RewardsCookieHostStatus = any;
export type RewardsHostStatus = any;

export interface RewardsCookieHostProps {
    isVisible: boolean;
    isCachingEnabled: boolean;
    onStatusChange: (status: RewardsHostStatus) => void;
}

const defaultExport: FunctionComponent<RewardsCookieHostProps> = observer(function RewardsCookieHost({ isVisible, isCachingEnabled, onStatusChange, }: RewardsCookieHostProps) {
    const [status, setStatus] = useState<RewardsCookieHostStatus>("loading");
    const [iframeUrl, setIframeUrl] = useState<string | undefined>(undefined);
    
    useEffect(() => {
        onStatusChange(status === "loading"
            ? "loading" : status === "ready"
            ? "ready" : "settled");
    }, [status, onStatusChange]);
    
    const flyoutWindowRef = useRef<Window | null>(null);
    const handleIframeLoad = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
        flyoutWindowRef.current = event.currentTarget.contentWindow;
    }, []);
    
    const { isCacheTrusted, markLoaded } = useRewardsCacheSafeWindow();
    const getCurrentIdentityKey = useCallback(() => {
        const moduleMailboxInfo = getModuleContextMailboxInfo();
        const coprincipalMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo);
        return getAccountKeyForMailboxInfo(coprincipalMailboxInfo ?? moduleMailboxInfo);
    }, []);
    
    const hasLoggedOpenRef = useRef(false);
    const [prevIsVisible, setPrevIsVisible] = useState(false);
    if (prevIsVisible !== isVisible) {
        setPrevIsVisible(isVisible);
        if (!isVisible) {
            hasLoggedOpenRef.current = false;
            if (!(isCachingEnabled && status === "ready")) {
                setStatus("loading");
                setIframeUrl(undefined);
            }
        }
        else if (isCachingEnabled &&
            status === "ready" &&
            iframeUrl !== undefined &&
            !isCacheTrusted(getCurrentIdentityKey())) {
            setStatus("loading");
            setIframeUrl(undefined);
        }
    }
    
    useEffect(() => {
        if (isCachingEnabled && isVisible && status === "ready") {
            if (!hasLoggedOpenRef.current) {
                hasLoggedOpenRef.current = true;
                const moduleMailboxInfo = getModuleContextMailboxInfo();
                const coprincipalMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo);
                logCoreUsage("RewardsPaneOpened", coprincipalMailboxInfo ?? moduleMailboxInfo, { outcome: "flyout", loadMs: 0 }, { isScorecardEvent: true });
            }
            markLoaded(getCurrentIdentityKey());
        }
    }, [isCachingEnabled, isVisible, status, markLoaded, getCurrentIdentityKey]);
    
    useEffect(() => {
        if (!isVisible || status !== "loading") {
            return;
        }
        flyoutWindowRef.current = null;
        let cancelled = false;
        const moduleMailboxInfo = getModuleContextMailboxInfo();
        const coprincipalMailboxInfo = getCoprincipalMailboxInfoForMailboxInfo(moduleMailboxInfo);
        const telemetryMailboxInfo = coprincipalMailboxInfo ?? moduleMailboxInfo;
        const isDarkMode = getIsDarkTheme();
        void (async () => {
            const loadStart = performance.now();
            const owaAnid = coprincipalMailboxInfo
                ? await getRewardsExpectedAnid(coprincipalMailboxInfo)
                : "";
            if (cancelled) {
                return;
            }
            const readyPromise = waitForFlyoutReady(() => flyoutWindowRef.current);
            setIframeUrl(getRewardsIframeUrl(isDarkMode, owaAnid));
            const flyoutResult = await readyPromise;
            if (cancelled) {
                return;
            }
            let nextStatus: RewardsCookieHostStatus;
            let outcome: "flyout" | "blocked" | "mismatch" | "error";
            if (flyoutResult.blocked) {
                nextStatus = "blocked";
                outcome = "blocked";
            }
            else if (flyoutResult.anidMatch !== true) {
                nextStatus = "mismatch";
                outcome = "mismatch";
            }
            else if (flyoutResult.flyoutError) {
                nextStatus = "error";
                outcome = "error";
            }
            else {
                nextStatus = "ready";
                outcome = "flyout";
            }
            logCoreUsage("RewardsPaneOpened", telemetryMailboxInfo, { outcome, loadMs: Math.round(performance.now() - loadStart) }, { isScorecardEvent: true });
            hasLoggedOpenRef.current = true;
            setStatus(nextStatus);
        })();
        return () => {
            cancelled = true;
        };
    }, [isVisible, status]);
    
    return status === "blocked" ? (<RewardsFallback variant="blocked"/>) : status === "mismatch" ? (<RewardsFallback variant="mismatch"/>) : status === "error" ? (<RewardsFallback variant="error"/>) : (<div className={iframeContainer}>
                {iframeUrl !== undefined && (<OWAIframe isVisible={isVisible} scenario="RewardsPane" src={iframeUrl} title={loc(rewardsPaneHeaderText)} style={rewardsIframeStyle} onLoad={handleIframeLoad} sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox" referrerPolicy="strict-origin-when-cross-origin"/>)}
                {status === "loading" && (<div className={loadingCover}>
                        <Spinner size="medium" labelPosition="below" label={loc(loadingRewardsText)}/>
                    </div>)}
            </div>);
}, "RewardsCookieHost");

export default defaultExport;
